import type { Controls } from '../core/device';
import { clamp } from '../core/math';
import type { Random } from '../core/Random';
import { banner, roundRect } from './draw';
import type { MiniGame, MiniInput } from './types';

/** The sweet spot: alcohol boils at 173°F, water at 212°F. */
export const STILL_ZONE: readonly [number, number] = [173, 208];
const MIN_T = 70;
const MAX_T = 290;
const DURATION = 30;
/** Above this, pressure builds. At full pressure: kaboom. */
const DANGER_T = 228;

/**
 * Running Uncle Rudy's still: tap to stoke the fire and keep the pot in the sweet spot.
 * Too cold and nothing drips; too hot and the pressure builds until the whole thing
 * blows (taking your eyebrows with it).
 */
export class StillGame implements MiniGame {
  readonly title = "Uncle Rudy's Still";
  readonly width = 360;
  readonly height = 240;
  temp = 95;
  fuel = 0.15;
  pressure = 0;
  /** Jars filled so far (fractional while one is filling). */
  progress = 0;
  boom = false;
  private t = 0;
  private overT = -1;
  private gustIn: number;
  private gust = 0;
  private stoke = 0;

  constructor(private readonly rng: Random) {
    this.gustIn = rng.range(5, 9);
  }

  /** 0..1 just after you stoke the fire. */
  get stoking(): number {
    return this.stoke;
  }

  get jars(): number {
    return Math.floor(this.progress);
  }

  get over(): boolean {
    return this.overT >= 0;
  }

  get done(): boolean {
    return this.over && this.t - this.overT > 1.2;
  }

  get timeLeft(): number {
    return Math.max(0, DURATION - this.t);
  }

  hint(controls: Controls): string {
    return `${{ keys: 'Tap Space/E', touch: 'Tap', pad: 'Tap A' }[controls]} to stoke the fire. Keep the needle in the green: 173-208°F. Don't let her blow.`;
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    this.stoke = Math.max(0, this.stoke - dt * 3);
    if (this.over) return;
    if (input.pressed) {
      this.fuel = Math.min(1, this.fuel + 0.2);
      this.stoke = 1;
    }
    this.fuel = Math.max(0, this.fuel - dt * 0.085);
    this.gustIn -= dt;
    if (this.gustIn <= 0) {
      // A gust through the holler: the fire flares or gutters.
      this.gustIn = this.rng.range(4, 8);
      this.gust = this.rng.chance(0.5) ? 1 : -1;
      this.fuel = clamp(this.fuel + this.gust * 0.18, 0, 1);
    }
    // The pot chases the fire's temperature.
    const target = MIN_T + this.fuel * 230;
    this.temp += (target - this.temp) * Math.min(1, dt * 0.55);
    this.temp = clamp(this.temp, MIN_T, MAX_T);

    const [lo, hi] = STILL_ZONE;
    if (this.temp >= lo && this.temp <= hi) this.progress += dt * 0.24;
    else if (this.temp > hi && this.temp < 214) this.progress += dt * 0.08;
    if (this.temp > DANGER_T) this.pressure += ((this.temp - DANGER_T) / 40) * dt * 1.4;
    else this.pressure = Math.max(0, this.pressure - dt * 0.12);

    if (this.pressure >= 1) {
      this.boom = true;
      this.overT = this.t;
    } else if (this.t >= DURATION) this.overT = this.t;
  }

  draw(g: CanvasRenderingContext2D, time: number): void {
    const W = this.width;
    const H = this.height;
    const sky = g.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, '#1d2418');
    sky.addColorStop(1, '#3a2e22');
    g.fillStyle = sky;
    g.fillRect(0, 0, W, H);
    // Trees in the dark.
    g.fillStyle = '#141a12';
    for (let i = 0; i < 7; i++) {
      const x = 10 + i * 52;
      g.beginPath();
      g.moveTo(x, 70);
      g.lineTo(x + 18, 10);
      g.lineTo(x + 36, 70);
      g.fill();
    }

    // Fire under the pot.
    const fire = this.boom ? 0 : this.fuel;
    for (let i = 0; i < 7; i++) {
      const fx = 70 + i * 14;
      const fh = (16 + fire * 40) * (0.6 + 0.4 * Math.sin(time * 12 + i * 1.7)) * (1 + this.stoke * 0.3);
      g.fillStyle = i % 2 ? '#ff7a1a' : '#ffc24a';
      g.beginPath();
      g.moveTo(fx - 8, 214);
      g.quadraticCurveTo(fx, 214 - fh * 1.2, fx + 8, 214);
      g.fill();
    }
    // The copper pot, the arm and the jar filling up.
    g.fillStyle = this.boom ? '#4a3a2a' : '#b87333';
    roundRect(g, 52, 120, 120, 92, 22);
    g.fill();
    g.fillStyle = 'rgba(255,255,255,0.18)';
    g.fillRect(64, 130, 12, 70);
    g.strokeStyle = '#b87333';
    g.lineWidth = 6;
    g.beginPath();
    g.moveTo(112, 120);
    g.lineTo(112, 104);
    g.lineTo(226, 140);
    g.stroke();
    const jarX = 214;
    g.fillStyle = 'rgba(210,230,236,0.35)';
    roundRect(g, jarX, 150, 34, 50, 6);
    g.fill();
    g.fillStyle = 'rgba(245,242,232,0.9)';
    const fill = this.progress - Math.floor(this.progress);
    g.fillRect(jarX + 3, 197 - 44 * fill, 28, 44 * fill);
    if (!this.boom && this.temp >= STILL_ZONE[0] && this.temp < 214) {
      g.fillStyle = '#f4f1e8';
      g.beginPath();
      g.arc(226, 146 + ((time * 60) % 10), 2.5, 0, Math.PI * 2);
      g.fill();
    }

    // Thermometer dial.
    const cx = 300;
    const cy = 110;
    const r = 44;
    const angle = (temp: number): number => Math.PI * (0.8 + 1.4 * ((temp - MIN_T) / (MAX_T - MIN_T)));
    g.fillStyle = '#eee8d5';
    g.beginPath();
    g.arc(cx, cy, r + 6, 0, Math.PI * 2);
    g.fill();
    const arc = (a: number, b: number, color: string): void => {
      g.strokeStyle = color;
      g.lineWidth = 8;
      g.beginPath();
      g.arc(cx, cy, r - 4, angle(a), angle(b));
      g.stroke();
    };
    arc(MIN_T, STILL_ZONE[0], '#7aa7d6');
    arc(STILL_ZONE[0], STILL_ZONE[1], '#3cbf4a');
    arc(STILL_ZONE[1], DANGER_T, '#f2a22a');
    arc(DANGER_T, MAX_T, '#e0352b');
    g.strokeStyle = '#111';
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(angle(this.temp)) * (r - 8), cy + Math.sin(angle(this.temp)) * (r - 8));
    g.stroke();
    g.fillStyle = '#111';
    g.font = '13px Anton, Impact, sans-serif';
    g.textAlign = 'center';
    g.fillText(`${Math.round(this.temp)}°F`, cx, cy + 24);

    // Pressure, jars, time.
    g.fillStyle = 'rgba(0,0,0,0.5)';
    g.fillRect(256, 176, 88, 10);
    g.fillStyle = this.pressure > 0.6 ? '#ff3b2a' : '#f2a22a';
    g.fillRect(256, 176, 88 * clamp(this.pressure, 0, 1), 10);
    g.fillStyle = '#fff';
    g.textAlign = 'left';
    g.fillText('PRESSURE', 256, 170);
    g.fillText(`JARS: ${this.jars}`, 14, 24);
    g.textAlign = 'right';
    g.fillText(`${Math.ceil(this.timeLeft)}s`, W - 14, 24);

    if (this.boom) {
      g.fillStyle = `rgba(255,200,120,${clamp(1 - (this.t - this.overT), 0, 1) * 0.8})`;
      g.fillRect(0, 0, W, H);
      banner(g, 'KA-BOOM!', W / 2, 80, '#ff6a3a', 48);
    } else if (this.over) {
      banner(g, `${this.jars} JAR${this.jars === 1 ? '' : 'S'}!`, W / 2, 80, '#ffd400', 42);
    }
  }
}
